import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { feature } from "topojson-client";
import type { FeatureCollection, Geometry } from "geojson";

const FIPS: Record<string, string> = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT",
  "10":"DE","11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN",
  "19":"IA","20":"KS","21":"KY","22":"LA","23":"ME","24":"MD","25":"MA",
  "26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE","32":"NV",
  "33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND","39":"OH",
  "40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD","47":"TN",
  "48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV","55":"WI",
  "56":"WY","72":"PR",
};

const STATE_NAMES: Record<string, string> = {
  AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",
  CO:"Colorado",CT:"Connecticut",DE:"Delaware",DC:"Washington D.C.",
  FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",
  IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",
  ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",
  MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",
  NH:"New Hampshire",NJ:"New Jersey",NM:"New Mexico",NY:"New York",
  NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",OR:"Oregon",
  PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",
  TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",
  WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",PR:"Puerto Rico",
};

const BLUE = new Set(["WA","OR","CA","NV","AZ","NM","CO","TX","AR","LA","MS","AL","GA","FL","SC","NC","NY","NH","MT","HI","PR"]);
const RED = new Set(["ID","WY","ND","SD","UT","VA"]);

// Theme colors (hex equivalents of our navy/red brand tokens)
const C_BLUE = "#1a3264";
const C_RED = "#c41230";
const C_GRAY = "#C8CDD4";

const StateActivityMap = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const runRef = useRef<() => void>(() => {});
  const [blueCount, setBlueCount] = useState(0);
  const [redCount, setRedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const clearTimers = () => { timers.forEach(clearTimeout); timers.length = 0; };

    (async () => {
      try {
        const [us, world] = await Promise.all([
          d3.json<any>("https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"),
          d3.json<any>("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json"),
        ]);
        if (!us || cancelled || !containerRef.current) return;

        const width = 960, height = 600;
        const projection = d3.geoAlbersUsa().scale(1280).translate([width / 2, height / 2]);
        const path = d3.geoPath().projection(projection);
        const fc = feature(us, us.objects.states) as unknown as FeatureCollection<Geometry, { name: string }>;
        const allFeatures = fc.features as any[];
        // Contiguous + AK + HI handled by AlbersUsa. PR is excluded (projection returns null).
        const states = allFeatures.filter(d => String(d.id).padStart(2, "0") !== "72");
        const prFeature = allFeatures.find(d => String(d.id).padStart(2, "0") === "72");

        // Canada & Mexico from world-atlas (numeric ids: 124 Canada, 484 Mexico)
        let canadaFeature: any = null;
        let mexicoFeature: any = null;
        if (world) {
          const worldFc = feature(world, world.objects.countries) as unknown as FeatureCollection<Geometry, { name: string }>;
          const wf = worldFc.features as any[];
          canadaFeature = wf.find(d => String(d.id) === "124");
          mexicoFeature = wf.find(d => String(d.id) === "484");
        }
        // A standard Albers projection roughly matching the contiguous-US portion of AlbersUsa,
        // so Canada appears above and Mexico below the US states naturally.
        const naProjection = d3.geoAlbers()
          .rotate([96, 0])
          .center([-0.6, 38.7])
          .parallels([29.5, 45.5])
          .scale(1280)
          .translate([width / 2, height / 2]);
        const naPath = d3.geoPath().projection(naProjection);

        const root = d3.select(containerRef.current);
        root.selectAll("svg").remove();
        const svg = root.append("svg").attr("viewBox", `-180 0 ${width + 240} ${height}`).attr("class", "w-full h-auto block");

        // Render Canada & Mexico BEHIND the US states so US borders sit on top.
        const naGroup = svg.append("g").attr("class", "na-countries");
        const canadaShape = naGroup.append("path")
          .attr("d", canadaFeature ? (naPath(canadaFeature) as string) : "")
          .attr("class", "country")
          .attr("stroke", "white").attr("stroke-width", 0.8)
          .attr("fill", C_GRAY);
        const mexicoShape = naGroup.append("path")
          .attr("d", mexicoFeature ? (naPath(mexicoFeature) as string) : "")
          .attr("class", "country")
          .attr("stroke", "white").attr("stroke-width", 0.8)
          .attr("fill", C_GRAY);

        const getAbbr = (d: any) => (d ? FIPS[String(d.id).padStart(2, "0")] || "" : "");

        // Use AlbersUsa for contiguous states only; override AK & HI with custom insets
        // placed in the lower-left so they don't overlap Mexico.
        const akFeature = states.find(d => getAbbr(d) === "AK");
        const hiFeature = states.find(d => getAbbr(d) === "HI");
        const contiguousStates = states.filter(d => {
          const a = getAbbr(d);
          return a !== "AK" && a !== "HI";
        });

        // Inset boxes for non-contiguous areas (AK, HI, PR, USVI) shown off the
        // California coast / lower-right to indicate they are not at true position.
        const akBox = { x: -170, y: 300, w: 180, h: 130 };
        const hiBox = { x: -130, y: 440, w: 180, h: 70 };

        // Alaska inset: bigger, centered inside its box
        const akProjection = d3.geoAlbers()
          .rotate([149, 0])
          .center([0, 63])
          .parallels([55, 65])
          .scale(420)
          .translate([akBox.x + akBox.w / 2, akBox.y + akBox.h / 2]);
        const akPath = d3.geoPath().projection(akProjection);

        // Hawaii inset: centered inside its box, just below Alaska
        const hiProjection = d3.geoMercator()
          .center([-157, 20.5])
          .scale(900)
          .translate([hiBox.x + hiBox.w / 2, hiBox.y + hiBox.h / 2]);
        const hiPath = d3.geoPath().projection(hiProjection);

        const paths = svg.selectAll<SVGPathElement, any>("path.state")
          .data(contiguousStates)
          .join("path")
          .attr("class", "state")
          .attr("d", path as any)
          .attr("stroke", "white").attr("stroke-width", 0.8)
          .attr("fill", C_GRAY);

        // Helper to draw an inset bounding box
        const drawInsetBox = (b: { x: number; y: number; w: number; h: number }) => {
          svg.append("rect")
            .attr("x", b.x).attr("y", b.y)
            .attr("width", b.w).attr("height", b.h)
            .attr("rx", 4).attr("ry", 4)
            .attr("fill", "none")
            .attr("stroke", C_GRAY).attr("stroke-width", 0.8);
        };

        drawInsetBox(akBox);
        drawInsetBox(hiBox);

        const akShape = svg.append("path")
          .datum(akFeature)
          .attr("class", "state")
          .attr("d", akFeature ? (akPath(akFeature) as string) : "")
          .attr("stroke", "white").attr("stroke-width", 0.8)
          .attr("fill", C_GRAY);

        const hiShape = svg.append("path")
          .datum(hiFeature)
          .attr("class", "state")
          .attr("d", hiFeature ? (hiPath(hiFeature) as string) : "")
          .attr("stroke", "white").attr("stroke-width", 0.8)
          .attr("fill", C_GRAY);

        // Inset: Puerto Rico (real geometry) + US Virgin Islands (stylized markers),
        // each in their own labeled-style box in the lower-right area.
        const insetGroup = svg.append("g").attr("class", "inset-pr-vi");

        const prBox = { x: 855, y: 525, w: 90, h: 60 };
        insetGroup.append("rect")
          .attr("x", prBox.x).attr("y", prBox.y)
          .attr("width", prBox.w).attr("height", prBox.h)
          .attr("rx", 4).attr("ry", 4)
          .attr("fill", "none")
          .attr("stroke", C_GRAY).attr("stroke-width", 0.8);

        const prProjection = d3.geoMercator()
          .center([-66.5, 18.22])
          .scale(2000)
          .translate([prBox.x + prBox.w / 2, prBox.y + prBox.h / 2]);
        const prPath = d3.geoPath().projection(prProjection);
        const prShape = insetGroup.append("path")
          .attr("d", prFeature ? (prPath(prFeature) as string) : "")
          .attr("class", "state")
          .attr("stroke", "white").attr("stroke-width", 0.6)
          .attr("fill", C_GRAY);

        // USVI box, to the right of Puerto Rico's box
        const viBoxX = 960, viBoxY = 540, viBoxW = 40, viBoxH = 36;
        insetGroup.append("rect")
          .attr("x", viBoxX).attr("y", viBoxY)
          .attr("width", viBoxW).attr("height", viBoxH)
          .attr("rx", 4).attr("ry", 4)
          .attr("fill", "none")
          .attr("stroke", C_GRAY).attr("stroke-width", 0.8);
        const viShapes = insetGroup.append("g").attr("class", "vi-shapes");
        const viCx = viBoxX + viBoxW / 2;
        const viCy = viBoxY + viBoxH / 2;
        const viItems = [
          // St. Thomas (rounded oblong)
          { type: "ellipse", cx: viCx - 6, cy: viCy - 4, rx: 4.0, ry: 1.5 },
          // St. John (small oblong, just east of St. Thomas)
          { type: "ellipse", cx: viCx + 2, cy: viCy - 3, rx: 1.8, ry: 1.0 },
          // St. Croix (larger oblong, south)
          { type: "ellipse", cx: viCx, cy: viCy + 6, rx: 5.0, ry: 1.7 },
        ];
        const viSel = viShapes.selectAll("ellipse")
          .data(viItems)
          .join("ellipse")
          .attr("cx", d => d.cx).attr("cy", d => d.cy)
          .attr("rx", d => d.rx).attr("ry", d => d.ry)
          .attr("stroke", "white").attr("stroke-width", 0.6)
          .attr("fill", C_GRAY);


        const runAnimation = () => {
          svg.selectAll<SVGPathElement, any>("path.state").interrupt().attr("fill", C_GRAY);
          prShape.interrupt().attr("fill", C_GRAY);
          viSel.interrupt().attr("fill", C_GRAY);
          canadaShape.interrupt().attr("fill", C_GRAY);
          mexicoShape.interrupt().attr("fill", C_GRAY);
          let blue = 0, red = 0;
          setBlueCount(0); setRedCount(0);

          const blueStates = states.filter(d => BLUE.has(getAbbr(d)));
          const redStates = states.filter(d => RED.has(getAbbr(d)));
          blueStates.sort((a, b) => (path.centroid(a)[0]) - (path.centroid(b)[0]));

          // Slower, subtler timing
          const blueStep = 110;
          const blueDur = 700;
          const redStep = 200;
          const redDur = 800;
          const startDelay = 300;

          blueStates.forEach((d, i) => {
            const abbr = getAbbr(d);
            timers.push(setTimeout(() => {
              svg.selectAll<SVGPathElement, any>("path.state")
                .filter(x => getAbbr(x) === abbr)
                .transition().duration(blueDur).ease(d3.easeCubicOut).attr("fill", C_BLUE);
              blue++; setBlueCount(blue);
            }, startDelay + i * blueStep));
          });

          const prDelay = startDelay + blueStates.length * blueStep;
          timers.push(setTimeout(() => {
            prShape.transition().duration(blueDur).attr("fill", C_BLUE);
            blue++; setBlueCount(blue);
          }, prDelay));

          // USVI shortly after PR
          timers.push(setTimeout(() => {
            viSel.transition().duration(blueDur).attr("fill", C_BLUE);
            blue++; setBlueCount(blue);
          }, prDelay + 180));

          const redStart = startDelay + blueStates.length * blueStep + 400;
          redStates.forEach((d, i) => {
            const abbr = getAbbr(d);
            timers.push(setTimeout(() => {
              svg.selectAll<SVGPathElement, any>("path.state")
                .filter(x => getAbbr(x) === abbr)
                .transition().duration(redDur).ease(d3.easeCubicOut).attr("fill", C_RED);
              red++; setRedCount(red);
            }, redStart + i * redStep));
          });

          // Color Canada and Mexico red toward the end of the red sequence
          const naStart = redStart + redStates.length * redStep;
          timers.push(setTimeout(() => {
            canadaShape.transition().duration(redDur).ease(d3.easeCubicOut).attr("fill", C_RED);
            red++; setRedCount(red);
          }, naStart));
          timers.push(setTimeout(() => {
            mexicoShape.transition().duration(redDur).ease(d3.easeCubicOut).attr("fill", C_RED);
            red++; setRedCount(red);
          }, naStart + 180));

          // Schedule loop: hold fully colored, then fade back to gray, then restart
          const fullyDoneAt = naStart + redDur + 200;
          const holdMs = 2500;
          const fadeOutMs = 1200;

          timers.push(setTimeout(() => {
            svg.selectAll<SVGPathElement, any>("path.state")
              .transition().duration(fadeOutMs).ease(d3.easeCubicInOut).attr("fill", C_GRAY);
            prShape.transition().duration(fadeOutMs).attr("fill", C_GRAY);
            viSel.transition().duration(fadeOutMs).attr("fill", C_GRAY);
            canadaShape.transition().duration(fadeOutMs).attr("fill", C_GRAY);
            mexicoShape.transition().duration(fadeOutMs).attr("fill", C_GRAY);
          }, fullyDoneAt + holdMs));

          timers.push(setTimeout(() => {
            if (!cancelled) runAnimation();
          }, fullyDoneAt + holdMs + fadeOutMs + 400));
        };

        runRef.current = runAnimation;
        setLoading(false);
        runAnimation();
      } catch (e) {
        console.error(e);
        if (!cancelled) { setError(true); setLoading(false); }
      }
    })();

    return () => {
      cancelled = true;
      clearTimers();
      if (container) d3.select(container).selectAll("svg").remove();
    };
  }, []);

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm p-6 lg:p-8 max-w-3xl mx-auto">
      <div ref={containerRef} className="min-h-[200px]">
        {loading && !error && (
          <div className="text-center py-16 text-muted-foreground text-sm">Loading map…</div>
        )}
        {error && null}
      </div>

      <div className="flex flex-wrap justify-center gap-6 mt-5 text-xs lg:text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 rounded-sm" style={{ background: C_BLUE }} />
          <span>States &amp; territories where Accion has conducted solicitations</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 rounded-sm" style={{ background: C_RED }} />
          <span>States &amp; countries from which Accion has evaluated Power Supply Sources</span>
        </div>
      </div>

      <div className="flex justify-center gap-12 mt-6">
        <div className="text-center">
          <div className="font-display text-3xl font-bold leading-none" style={{ color: C_BLUE }}>{blueCount}</div>
          <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mt-1">Solicitation states &amp; territories</div>
        </div>
        <div className="text-center">
          <div className="font-display text-3xl font-bold leading-none" style={{ color: C_RED }}>{redCount}</div>
          <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mt-1">Power supply states &amp; countries</div>
        </div>
      </div>

    </div>
  );
};

export default StateActivityMap;
